import gridMixin from 'v/Story/Timeline/view/grid.mixin';
import { mapActions, mapState } from 'vuex';
import fg from '#/libs/FrameGrid';
import {
  assignDurations,
  getAudioSnapTimesLeft,
  getAudioSnapTimesRight,
  CursorManager,
} from 'v/Story/Timeline/api/tapi';
import tmBus from 'v/Story/Timeline/api/tmBus';
import moveAudioMultiple from 'v/Story/Timeline/view/movable/moveAudioMultiple';

export default {
  mixins: [gridMixin, moveAudioMultiple],

  computed: {
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      MODE_OPTIONAL_AUDIO_INSERT: (state) => state.MODE.OPTIONAL_AUDIO_INSERT,
      MODE_AUDIO_MULTISELECT: (state) => state.MODE.AUDIO_MULTISELECT,
      trackLayer: (state) => state.trackLayer,
      diagnosticEscort: (state) => state.diagnosticEscort,
      multiSelectIndex: (state) => state.dndEscort.multiSelectIndex,
      secondhand: (state) => state.secondhand,
    }),

    trackIndex() {
      return this.trackLayer.audio.indexOf(this.$props.track);
    },

    moveObj() {
      let enabled = !this.MODE_SCENE_EDIT;
      if (this.MODE_AUDIO_MULTISELECT) {
        enabled = this.trackIndex >= this.multiSelectIndex;
      }

      return {
        enabled,
        distance2trigger: this.CONST.DND_SENSITIVITY.DEFAULT,
        onMoveStart: this.MODE_AUDIO_MULTISELECT
          ? this.$_onMoveMultipleStart
          : this.$_onMoveStart,
        onMove: this.MODE_AUDIO_MULTISELECT
          ? this.$_onMoveMultiple
          : this.$_onMove,
        onMoveEnd: this.MODE_AUDIO_MULTISELECT
          ? this.$_onMoveMultipleEnd
          : this.$_onMoveEnd,
      };
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'MANAGED_AUDIO_INSERT_EXIT',
      'SET_MODE',
      'SET_DND_ESCORT',
      'UNSELECT_TRACKS',
      'SELECT_AUDIO',
      'SET_AUDIO_LAYER_DURATION',
      'RECALCULATE_ROWS',
    ]),

    $_onMoveStart(state) {
      if (this.MODE_OPTIONAL_AUDIO_INSERT) {
        this.MANAGED_AUDIO_INSERT_EXIT();
      }

      this.SET_MODE({ name: 'AUDIO_DND', value: true });

      state.mousePoint = this.$_mouse2point(state.eDown);
      state.track = this.$props.track;

      state.track.isMoving = true;
      state.initial = assignDurations({}, state.track.data);
      state.duration = fg.trunc(
        state.track.data.displayEndTime - state.track.data.displayStartTime
      );
      state.shiftX = state.eDown.x - state.eMove.x;
      state.timeShift = state.mousePoint.time - state.initial.displayStartTime;

      state.proximityTester = {
        left: this.$_createProximityTester(
          [
            this.secondhand,
            ...getAudioSnapTimesLeft({
              trackLayer: this.trackLayer,
              exceptGuid: state.track.guid,
            }),
          ],
          this.CONST.AUTOSNAP_PROXIMITY.AUDIO
        ),
        right: this.$_createProximityTester(
          [
            this.secondhand,
            ...getAudioSnapTimesRight({
              trackLayer: this.trackLayer,
              exceptGuid: state.track.guid,
            }),
          ],
          this.CONST.AUTOSNAP_PROXIMITY.AUDIO
        ),
      };

      state.maxDuration = this.trackLayer.audioDuration;
      state.moveAreas = this.$_mapMoveAreas(state);

      this.UNSELECT_TRACKS();
      this.SELECT_AUDIO({ track: state.track });
      this.SET_DND_ESCORT({ name: 'trackInHand', value: state.track });
      this.SET_DND_ESCORT({ name: 'useScrollSensor', value: true });

      tmBus.$emit('callbackBeforeDragStarts');
      CursorManager.set('grabbing');
    },

    $_mapMoveAreas(state) {
      const required = state.duration;
      const tracks = _.filter(
        this.trackLayer.audio,
        (t) => t.guid !== state.track.guid
      );
      const lastFreeStart = fg.trunc(
        tracks[tracks.length - 1]?.data.displayEndTime ?? 0
      );
      const rv = [{ free: true, start: lastFreeStart, end: Infinity }];

      // @note: iterating from right to left, so the "next" here is next from left
      for (let n = tracks.length - 1; n >= 0; n--) {
        const cur = tracks[n].data;
        const next = tracks[n - 1]?.data;
        const isEnd = fg.trunc(cur.displayStartTime - required) < 0;
        const leftMargin = Math.max(
          0,
          fg.trunc(cur.displayStartTime - required)
        );

        // enlarge or set prev taken area
        const prevTakenArea = rv[0];
        if (prevTakenArea && !prevTakenArea.free) {
          prevTakenArea.start = leftMargin;
        } else {
          rv.unshift({
            free: false,
            start: leftMargin,
            end: cur.displayEndTime,
          });
        }

        // add free area if margin with next track is enough
        if (next) {
          if (leftMargin >= next.displayEndTime) {
            rv.unshift({
              free: true,
              start: next.displayEndTime,
              end: leftMargin,
            });
          }
        } else if (leftMargin >= 0 && !isEnd) {
          rv.unshift({ free: true, start: 0, end: leftMargin });
        }
      }

      return rv;
    },

    $_onMove(state) {
      const { data } = state.track;

      state.mousePoint = this.$_mouse2point(
        state.eMove,
        state.mousePoint,
        state.shiftX
      );
      // this.diagnosticEscort.tp1 = state.mousePoint;
      state.curStartTime = state.mousePoint.time - state.timeShift;

      let snapTime = state.proximityTester.left.find(state.curStartTime);
      if (null !== snapTime) {
        state.curStartTime = snapTime;
      } else {
        const endTime = fg.trunc(state.curStartTime + state.duration);
        snapTime = state.proximityTester.right.find(endTime);
        if (null !== snapTime) {
          state.curStartTime = fg.trunc(snapTime - state.duration);
        }
      }

      const i = _.findIndexInSorted(state.moveAreas, (area) => {
        return area.free
          ? state.curStartTime < area.start
            ? -1
            : area.end < state.curStartTime
            ? 1
            : 0
          : state.curStartTime <= area.start
          ? -1
          : area.end <= state.curStartTime
          ? 1
          : 0;
      });
      const area = state.moveAreas[i];

      let canFit = false;
      if (area && area.free) {
        canFit = true;
      } else if (area && !area.free) {
        if (0 === i) {
          state.curStartTime = area.end;
          canFit = true;
        } else if (i > 0) {
          const distance2left = state.curStartTime - area.start;
          const distance2right = area.end - state.curStartTime;
          if (distance2left < distance2right) {
            state.curStartTime = area.start;
          } else {
            state.curStartTime = area.end;
          }
          canFit = true;
        }
      }

      if (canFit) {
        data.displayStartTime = fg.trunc(state.curStartTime);
        data.displayEndTime = fg.trunc(data.displayStartTime + state.duration);
      }

      if (state.maxDuration < data.displayEndTime) {
        this.SET_AUDIO_LAYER_DURATION();
        this.RECALCULATE_ROWS();
        state.maxDuration = data.displayEndTime;
      }
    },

    $_onMoveEnd(state) {
      CursorManager.restore();
      let isChanged = true;

      if (state.canceled) {
        assignDurations(state.track.data, state.initial);
        isChanged = false;
      }

      this.SET_AUDIO_LAYER_DURATION();
      this.RECALCULATE_ROWS();

      state.track.isMoving = false;
      this.SET_DND_ESCORT({ name: 'trackInHand', value: null });
      this.SET_DND_ESCORT({ name: 'useScrollSensor', value: false });
      this.SET_MODE({ name: 'AUDIO_DND', value: false });

      if (isChanged) {
        tmBus.$emit('callbackChange', {
          e: state.eUp,
        });
      }
    },
  },
};
